import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getSaTenants,
  SaTenantListItem,
} from "../../../helpers/super_admin_ops_helper";

const PER_PAGE = 25;

const channelBadge = (
  channel: string | undefined,
  t: (key: string) => string
) => {
  if (channel === "brdominio") {
    return <Badge color="info">{t("sa.tenants.channel.brdominio")}</Badge>;
  }
  return (
    <Badge color="secondary">{t("sa.tenants.channel.selfServe")}</Badge>
  );
};

/**
 * Lista de tenants SA — criar + filtros canal/cap (LAC-053 · LAC-088).
 */
const SuperAdminTenantsList = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState<SaTenantListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [appliedSearch, setAppliedSearch] = useState(
    searchParams.get("search") || ""
  );
  const [blocked, setBlocked] = useState(searchParams.get("blocked") || "");
  const [billingChannel, setBillingChannel] = useState(
    searchParams.get("billing_channel") || ""
  );
  const [atDailyCap, setAtDailyCap] = useState(
    searchParams.get("at_daily_cap") === "1" ||
      searchParams.get("at_daily_cap") === "true"
      ? "1"
      : ""
  );
  const [preferredCurrency, setPreferredCurrency] = useState(
    searchParams.get("preferred_billing_currency") || ""
  );
  // Deep-link do modal de planos — clientes neste plano (active/past_due).
  const [planIdFilter, setPlanIdFilter] = useState(
    searchParams.get("plan_id") || ""
  );

  document.title = t("sa.tenants.documentTitle");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const planIdNum = planIdFilter ? Number(planIdFilter) : undefined;
      const res = await getSaTenants({
        page,
        per_page: PER_PAGE,
        search: appliedSearch || undefined,
        blocked: blocked || undefined,
        billing_channel: billingChannel || undefined,
        preferred_billing_currency: preferredCurrency || undefined,
        at_daily_cap: atDailyCap || undefined,
        plan_id:
          planIdNum && Number.isFinite(planIdNum) && planIdNum > 0
            ? planIdNum
            : undefined,
      });
      if (!res?.success) {
        setError(t("sa.tenants.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (e: any) {
      setError(e?.message || t("sa.tenants.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    appliedSearch,
    blocked,
    billingChannel,
    preferredCurrency,
    atDailyCap,
    planIdFilter,
    t,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (appliedSearch) next.set("search", appliedSearch);
    if (blocked) next.set("blocked", blocked);
    if (billingChannel) next.set("billing_channel", billingChannel);
    if (preferredCurrency) next.set("preferred_billing_currency", preferredCurrency);
    if (atDailyCap) next.set("at_daily_cap", atDailyCap);
    if (planIdFilter) next.set("plan_id", planIdFilter);
    setSearchParams(next, { replace: true });
  }, [
    appliedSearch,
    blocked,
    billingChannel,
    preferredCurrency,
    atDailyCap,
    planIdFilter,
    setSearchParams,
  ]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.tenants.title")}
          pageTitle={t("sa.menu.tenants")}
        />

        {error && (
          <Alert color="danger" className="d-flex justify-content-between gap-2">
            <span>{error}</span>
            <Button color="light" size="sm" onClick={() => load()}>
              {t("sa.tenants.retry")}
            </Button>
          </Alert>
        )}

        {planIdFilter ? (
          <Alert
            color="info"
            className="d-flex flex-wrap align-items-center justify-content-between gap-2"
          >
            <span>{t("sa.tenants.filter.planIdBanner", { id: planIdFilter })}</span>
            <Button
              color="info"
              size="sm"
              outline
              onClick={() => {
                setPlanIdFilter("");
                setPage(1);
              }}
            >
              {t("sa.tenants.filter.planIdClear")}
            </Button>
          </Alert>
        ) : null}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Input
                    type="search"
                    bsSize="sm"
                    style={{ maxWidth: 240 }}
                    placeholder={t("sa.tenants.searchPlaceholder")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setPage(1);
                        setAppliedSearch(search.trim());
                      }
                    }}
                  />
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => {
                      setPage(1);
                      setAppliedSearch(search.trim());
                    }}
                  >
                    {t("sa.tenants.searchButton")}
                  </Button>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ maxWidth: 180 }}
                    value={blocked}
                    onChange={(e) => {
                      setBlocked(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">{t("sa.tenants.filter.all")}</option>
                    <option value="1">{t("sa.tenants.filter.blocked")}</option>
                    <option value="0">{t("sa.tenants.filter.active")}</option>
                  </Input>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ maxWidth: 200 }}
                    value={billingChannel}
                    onChange={(e) => {
                      setBillingChannel(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">{t("sa.tenants.filter.channelAll")}</option>
                    <option value="brdominio">
                      {t("sa.tenants.channel.brdominio")}
                    </option>
                    <option value="self_serve">
                      {t("sa.tenants.channel.selfServe")}
                    </option>
                  </Input>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ maxWidth: 200 }}
                    value={preferredCurrency}
                    onChange={(e) => {
                      setPreferredCurrency(e.target.value);
                      setPage(1);
                    }}
                    aria-label={t("sa.tenants.filter.market")}
                  >
                    <option value="">{t("sa.tenants.filter.marketAll")}</option>
                    <option value="BRL">{t("sa.tenants.detail.marketBr")} (BRL)</option>
                    <option value="USD">{t("sa.tenants.detail.marketIntl")} (USD)</option>
                  </Input>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ maxWidth: 220 }}
                    value={atDailyCap}
                    onChange={(e) => {
                      setAtDailyCap(e.target.value);
                      setPage(1);
                    }}
                    aria-label={t("sa.tenants.filter.atDailyCap")}
                  >
                    <option value="">
                      {t("sa.tenants.filter.atDailyCapAll")}
                    </option>
                    <option value="1">
                      {t("sa.tenants.filter.atDailyCapOnly")}
                    </option>
                  </Input>
                </div>
                <Button
                  color="primary"
                  size="sm"
                  tag={Link}
                  to="/super-admin/tenants/new"
                >
                  {t("sa.tenants.newCustomer")}
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">
                      {atDailyCap
                        ? t("sa.tenants.emptyAtDailyCap")
                        : t("sa.tenants.empty")}
                    </p>
                    {!atDailyCap ? (
                    <Button
                      color="primary"
                      size="sm"
                      tag={Link}
                      to="/super-admin/tenants/new"
                    >
                      {t("sa.tenants.newCustomer")}
                    </Button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <p className="text-muted small mb-2">
                      {t("sa.tenants.totalCount", { count: total })}
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("sa.tenants.table.name")}</th>
                            <th>{t("sa.tenants.table.channel")}</th>
                            <th>{t("sa.tenants.table.market")}</th>
                            <th>{t("sa.tenants.table.from")}</th>
                            <th>{t("sa.tenants.table.setup")}</th>
                            <th>{t("sa.tenants.table.status")}</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((row) => (
                            <tr key={row.id}>
                              <td className="fw-medium">
                                {row.name}
                                {row.at_daily_cap ? (
                                  <Badge
                                    color="warning"
                                    className="ms-2"
                                    pill
                                    title={
                                      row.daily_cap != null
                                        ? t("sa.tenants.badge.atDailyCapTitle", {
                                            sent: Number(
                                              row.sent_today ?? 0
                                            ).toLocaleString(),
                                            cap: Number(
                                              row.daily_cap
                                            ).toLocaleString(),
                                          })
                                        : t("sa.tenants.badge.atDailyCap")
                                    }
                                  >
                                    {t("sa.tenants.badge.atDailyCap")}
                                  </Badge>
                                ) : null}
                              </td>
                              <td>{channelBadge(row.billing_channel, t)}</td>
                              <td>
                                <Badge
                                  color={
                                    (row.preferred_billing_currency || "BRL") ===
                                    "USD"
                                      ? "primary"
                                      : "light"
                                  }
                                >
                                  {row.preferred_billing_currency || "BRL"}
                                </Badge>
                              </td>
                              <td>{row.from_email || "—"}</td>
                              <td>
                                <Badge
                                  color={
                                    row.sending_setup_ready
                                      ? "success"
                                      : "warning"
                                  }
                                >
                                  {row.sending_setup_ready
                                    ? t("sa.tenants.setup.ready")
                                    : t("sa.tenants.setup.pending")}
                                </Badge>
                              </td>
                              <td>
                                <Badge
                                  color={row.blocked ? "danger" : "success"}
                                >
                                  {row.blocked
                                    ? t("sa.tenants.status.blocked")
                                    : t("sa.tenants.status.active")}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Link
                                  to={`/super-admin/tenants/${row.id}`}
                                  className="btn btn-sm btn-soft-primary"
                                >
                                  {t("sa.tenants.open")}
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {lastPage > 1 && (
                      <div className="d-flex justify-content-between mt-3">
                        <Button
                          color="light"
                          size="sm"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          {t("sa.tenants.prev")}
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          disabled={page >= lastPage}
                          onClick={() =>
                            setPage((p) => Math.min(lastPage, p + 1))
                          }
                        >
                          {t("sa.tenants.next")}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SuperAdminTenantsList;
